import { Injectable } from '@angular/core';
import * as forge from 'node-forge';
import { EncryptedPayloadBody } from './cryptography.model';
import { Observable } from 'rxjs';
import { API } from 'src/app/constants/paths';
import { ApiResponse } from 'src/app/interfaces/api';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CryptographyService {
  private publicKeyPemBase64!: string;

  constructor(private readonly http: HttpClient) {}

  initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getPublicKey().subscribe({
        next: response => {
          this.publicKeyPemBase64 = response.data;
          resolve();
        },
        error: error => {
          console.error('Failed to initialize public key:', error);
          reject(error);
        },
      });
    });
  }

  getPublicKey(): Observable<ApiResponse<string>> {
    return this.http.get<ApiResponse<string>>(
      API.PAYMENT_SERVICE_HOST + API.routes.meta
    );
  }

  async ensureInitialized(): Promise<void> {
    if (!this.publicKeyPemBase64) {
      console.warn('Public key is not initialized. Initializing now...');
      await this.initialize();
    }
  }

  get publicKeyPem(): string {
    return window.atob(this.publicKeyPemBase64);
  }

  encryptPayload(data: string): EncryptedPayloadBody | null {
    if (!this.publicKeyPemBase64) {
      console.error('Public key is not initialized.');
      return null;
    }

    try {
      // 1. Generate a random AES key (128-bit)
      const aesKey = forge.random.getBytesSync(16);

      // 2. Encrypt the data with AES-CBC
      const iv = forge.random.getBytesSync(16);
      const cipher = forge.cipher.createCipher('AES-CBC', aesKey);
      cipher.start({ iv });
      cipher.update(forge.util.createBuffer(data, 'utf8'));
      cipher.finish();
      const encryptedData = cipher.output.getBytes();

      // 3. Encrypt the AES key with RSA-OAEP
      const publicKey = forge.pki.publicKeyFromPem(this.publicKeyPem);
      const encryptedKey = publicKey.encrypt(aesKey, 'RSA-OAEP');

      // 4. Return base64-encoded encrypted parts
      return {
        request: forge.util.encode64(encryptedData),
        vk: `${forge.util.encode64(encryptedKey)}~${forge.util.encode64(iv)}`,
      };
    } catch (err) {
      console.error('Encryption failed:', err);
      return null;
    }
  }
}
